import React, { useEffect } from 'react';
import { useParams, Navigate, Link } from 'react-router-dom';
import { Calendar, Clock, User, ChevronLeft, ArrowLeft } from 'lucide-react';
import { blogData } from '../data/blogData';
import { SEOHead } from '../seo';
import { AdUnit } from './AdUnit';

export const BlogPost = () => {
  const { slug } = useParams();
  const post = blogData.find(p => p.slug === slug);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  if (!post) {
    return <Navigate to="/blog" replace />;
  }

  return (
    <div style={{ maxWidth: '800px', margin: '0 auto', width: '100%', padding: '0 0 60px 0' }}>
      <SEOHead
        title={post.seoTitle}
        description={post.seoDescription}
        keywords={post.keywords}
        path={`/blog/${post.slug}`}
        type="article"
        image={post.image}
      />

      {/* Back Navigation */}
      <Link 
        to="/blog"
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '8px',
          color: 'var(--text-secondary)',
          textDecoration: 'none',
          fontWeight: 600,
          fontSize: '0.9rem',
          marginBottom: '32px',
          transition: 'color 0.2s'
        }}
        onMouseEnter={(e) => e.currentTarget.style.color = 'var(--primary)'}
        onMouseLeave={(e) => e.currentTarget.style.color = 'var(--text-secondary)'}
      >
        <ArrowLeft size={16} /> Back to Blog
      </Link>

      {/* Article Header */}
      <header style={{ marginBottom: '40px' }}>
        <div style={{
          display: 'inline-block',
          background: 'rgba(5, 117, 251, 0.1)',
          color: 'var(--primary)',
          padding: '6px 14px',
          borderRadius: '99px',
          fontSize: '0.75rem',
          fontWeight: 800,
          textTransform: 'uppercase',
          letterSpacing: '0.05em',
          marginBottom: '20px'
        }}>
          {post.category}
        </div>

        <h1 style={{
          fontSize: '2.5rem',
          fontWeight: 900,
          color: 'var(--text-primary)',
          lineHeight: 1.2,
          margin: '0 0 24px 0',
          letterSpacing: '-0.02em'
        }}>
          {post.title}
        </h1>

        <div style={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          gap: '24px',
          color: 'var(--text-muted)',
          fontSize: '0.9rem',
          fontWeight: 600
        }}>
          <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <User size={16} /> {post.author}
          </span>
          <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <Calendar size={16} /> {new Date(post.date).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
          </span>
          <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <Clock size={16} /> {post.readTime}
          </span>
        </div>
      </header>

      {/* Hero Image */}
      <div style={{
        width: '100%',
        height: '400px',
        borderRadius: '24px',
        overflow: 'hidden',
        marginBottom: '48px',
        boxShadow: 'var(--shadow-md)',
        background: 'var(--bg-secondary)'
      }}>
        <img 
          src={post.image} 
          alt={post.title}
          onError={(e) => {
            if (!e.currentTarget.dataset.retried) {
              e.currentTarget.dataset.retried = "true";
              e.currentTarget.src = `https://picsum.photos/seed/${post.slug}/800/500`;
            } else {
              e.currentTarget.src = "/logo512.png";
            }
          }}
          style={{ width: '100%', height: '100%', objectFit: 'cover' }}
        />
      </div>

      {/* Top Ad Unit */}
      <AdUnit />

      {/* Article Content */}
      <article 
        className="blog-content"
        dangerouslySetInnerHTML={{ __html: post.content }}
        style={{
          fontSize: '1.05rem',
          lineHeight: 1.8,
          color: 'var(--text-secondary)',
          marginTop: '40px'
        }}
      />

      {/* Bottom Ad Unit */}
      <div style={{ marginTop: '60px' }}>
        <AdUnit />
      </div>

      <style>{`
        .blog-content h2 {
          color: var(--text-primary);
          font-size: 1.75rem;
          font-weight: 800;
          margin: 48px 0 20px 0;
          letter-spacing: -0.01em;
        }
        .blog-content h3 {
          color: var(--text-primary);
          font-size: 1.35rem;
          font-weight: 700;
          margin: 32px 0 16px 0;
        }
        .blog-content p {
          margin-bottom: 24px;
        }
        .blog-content ul, .blog-content ol {
          margin-bottom: 24px;
          padding-left: 24px;
        }
        .blog-content li {
          margin-bottom: 12px;
        }
        .blog-content strong {
          color: var(--text-primary);
          font-weight: 700;
        }
        .blog-content a {
          color: var(--primary);
          text-decoration: none;
          font-weight: 600;
          border-bottom: 2px solid transparent;
          transition: border-color 0.2s;
        }
        .blog-content a:hover {
          border-bottom-color: var(--primary);
        }
        .blog-content pre {
          background: rgba(15, 23, 42, 0.05);
          padding: 16px;
          border-radius: 12px;
          overflow-x: auto;
          margin-bottom: 24px;
        }
        .blog-content code {
          font-family: monospace;
          background: rgba(15, 23, 42, 0.05);
          padding: 2px 6px;
          border-radius: 4px;
          font-size: 0.9em;
          color: #ef4444;
        }
        .blog-content pre code {
          background: transparent;
          padding: 0;
          color: var(--text-primary);
        }
      `}</style>
    </div>
  );
};

export default BlogPost;
